import { useState } from 'react';
import Navbar from '../shop/components/Navbar';
import Footer from '../home/components/Footer';
import ProductCard from '../shop/components/ProductCard';
import { products } from '../../mocks/products';

export default function NewArrivalsPage() {
  const [sortBy, setSortBy] = useState('newest');

  // Filter only new products and sort
  const newProducts = products
    .filter((p) => p.isNew)
    .sort((a, b) => {
      if (sortBy === 'price-low') return a.price - b.price;
      if (sortBy === 'price-high') return b.price - a.price;
      return b.id - a.id; // newest first
    });

  const featuredProduct = newProducts[0];
  const otherProducts = newProducts.slice(1);

  // Calculate drop date (7 days ago for demo)
  const dropDate = new Date();
  dropDate.setDate(dropDate.getDate() - 7);
  const formattedDate = dropDate.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />

      {/* Hero Header */}
      <section className="relative pt-32 pb-20 px-8 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-white/5 via-transparent to-transparent"></div>
        
        <div className="max-w-[1600px] mx-auto relative z-10">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-3 px-6 py-2 bg-white/10 border border-white/20 rounded-full mb-6">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              <span className="text-white text-xs font-black tracking-[0.2em] uppercase">
                JUST DROPPED
              </span>
            </div>
            
            <h1 className="text-7xl md:text-8xl lg:text-9xl font-black text-white mb-6 tracking-tight">
              LATEST DROP
            </h1>
            
            <p className="text-gray-400 text-lg md:text-xl font-medium mb-2">
              Fresh styles. Bold statements. Limited quantities.
            </p>
            <p className="text-gray-500 text-sm font-bold tracking-wide">
              DROPPED {formattedDate.toUpperCase()}
            </p>
          </div>

          {/* Stats Bar */}
          <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16 mt-12">
            <div className="text-center">
              <p className="text-4xl font-black text-white mb-1">{newProducts.length}</p>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-gray-500">
                New Pieces
              </p>
            </div>
            <div className="w-px h-12 bg-white/10"></div>
            <div className="text-center">
              <p className="text-4xl font-black text-white mb-1">LIMITED</p>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-gray-500">
                Quantities
              </p>
            </div>
            <div className="w-px h-12 bg-white/10"></div>
            <div className="text-center">
              <p className="text-4xl font-black text-white mb-1">100%</p>
              <p className="text-xs font-bold tracking-[0.15em] uppercase text-gray-500">
                Authentic
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Hero Product */}
      {featuredProduct && (
        <section className="px-8 pb-20">
          <div className="max-w-[1600px] mx-auto">
            <div className="bg-gradient-to-br from-[#1A1A1A] to-[#0F0F0F] rounded-2xl overflow-hidden border border-white/10">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Image Side */}
                <div className="relative h-[500px] md:h-[700px] overflow-hidden" data-product-shop>
                  <img
                    src={featuredProduct.image}
                    alt={featuredProduct.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute top-6 left-6">
                    <div className="bg-white text-black px-4 py-2 rounded-full text-xs font-black tracking-wide">
                      FEATURED DROP
                    </div>
                  </div>
                </div>

                {/* Content Side */}
                <div className="p-12 md:p-16 flex flex-col justify-center">
                  <div className="mb-6">
                    <p className="text-xs font-bold tracking-[0.2em] uppercase text-gray-500 mb-3">
                      {featuredProduct.category}
                    </p>
                    <h2 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
                      {featuredProduct.name}
                    </h2>
                    <p className="text-gray-400 text-lg leading-relaxed mb-8">
                      The statement piece of the season. Premium construction meets bold design. 
                      Limited quantities available. Once they're gone, they're gone.
                    </p>
                  </div>

                  <div className="flex items-center gap-6 mb-10">
                    <div>
                      <p className="text-5xl font-black text-white">${featuredProduct.price}</p>
                    </div>
                    <div className="flex gap-2">
                      {featuredProduct.sizes.map((size) => (
                        <span
                          key={size}
                          className="px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-sm font-bold text-white"
                        >
                          {size}
                        </span>
                      ))}
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row gap-4">
                    <button className="flex-1 px-8 py-4 bg-white text-black rounded-lg font-black text-base hover:scale-105 transition-all whitespace-nowrap">
                      SHOP NOW
                    </button>
                    <button className="px-8 py-4 border-2 border-white/20 text-white rounded-lg font-black text-base hover:bg-white/5 transition-all whitespace-nowrap">
                      VIEW DETAILS
                    </button>
                  </div>

                  <div className="mt-10 pt-10 border-t border-white/10">
                    <div className="flex items-center gap-8">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-full">
                          <i className="ri-truck-line text-white text-lg"></i>
                        </div>
                        <div>
                          <p className="text-white text-sm font-bold">Free Shipping</p>
                          <p className="text-gray-500 text-xs">On orders over $100</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 flex items-center justify-center bg-white/5 rounded-full">
                          <i className="ri-shield-check-line text-white text-lg"></i>
                        </div>
                        <div>
                          <p className="text-white text-sm font-bold">Authentic</p>
                          <p className="text-gray-500 text-xs">100% Genuine</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* All New Arrivals Grid */}
      <section className="px-8 pb-32">
        <div className="max-w-[1600px] mx-auto">
          {/* Section Header with Sort */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6 mb-12">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-2">
                All New Arrivals
              </h2>
              <p className="text-gray-400 text-sm">
                Showing <span className="text-white font-bold">{newProducts.length}</span> new products
              </p>
            </div>

            {/* Sort Dropdown */}
            <div className="flex items-center gap-3">
              <label className="text-sm font-bold text-gray-400 whitespace-nowrap">
                SORT BY:
              </label>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="px-4 py-3 bg-[#1A1A1A] border border-white/20 rounded-lg text-white text-sm font-bold focus:outline-none focus:border-white/40 transition-all cursor-pointer"
              >
                <option value="newest">Newest First</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
              </select>
            </div>
          </div>

          {/* Product Grid */}
          {otherProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-product-shop>
              {otherProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="py-24 text-center">
              <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center bg-white/5 rounded-full">
                <i className="ri-shopping-bag-line text-4xl text-gray-600"></i>
              </div>
              <h3 className="text-2xl font-black text-white mb-3">More Drops Coming Soon</h3>
              <p className="text-gray-400 mb-8">Stay tuned for the next release</p>
            </div>
          )}
        </div>
      </section>

      {/* Hype Banner */}
      <section className="px-8 pb-32">
        <div className="max-w-[1600px] mx-auto">
          <div className="relative rounded-2xl overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-r from-black via-black/90 to-transparent z-10"></div>
            <img
              src="https://public.readdy.ai/ai/img_res/5c398ff0ba52ed682548351de52c3a7e.jpg"
              alt="Next Drop"
              className="w-full h-[400px] object-cover object-center"
            />
            <div className="absolute inset-0 z-20 flex items-center">
              <div className="px-12 md:px-20 max-w-2xl">
                <p className="text-xs font-black tracking-[0.2em] uppercase text-gray-400 mb-4">
                  COMING NEXT
                </p>
                <h2 className="text-5xl md:text-6xl font-black text-white mb-6 leading-tight">
                  Don't Miss The Next Drop
                </h2>
                <p className="text-gray-300 text-lg mb-8">
                  Be the first to know when new styles hit. Sign up for drop alerts and exclusive early access.
                </p>
                <button className="px-8 py-4 bg-white text-black rounded-lg font-black text-base hover:scale-105 transition-all whitespace-nowrap">
                  GET NOTIFIED
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}